// src/unique-id.ts
import { combineTransactionSteps, Extension, findChildren, findChildrenInRange, getChangedRanges } from "@tiptap/core";
import { Fragment, Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { v4 as uuidv4 } from "uuid";

// src/helpers/removeDuplicates.ts
function removeDuplicates(array, by = JSON.stringify) {
  const seen = {};
  return array.filter((item) => {
    const key = by(item);
    return Object.prototype.hasOwnProperty.call(seen, key) ? false : seen[key] = true;
  });
}

// src/helpers/findDuplicates.ts
function findDuplicates(items) {
  const filtered = items.filter((el, index) => items.indexOf(el) !== index);
  const duplicates = removeDuplicates(filtered);
  return duplicates;
}

// src/unique-id.ts
var UniqueID = Extension.create({
  name: "uniqueID",
  // we’ll set a very high priority to make sure this runs first
  // and is compatible with `appendTransaction` hooks of other extensions
  priority: 1e4,
  addOptions() {
    return {
      attributeName: "id",
      types: [],
      generateID: () => uuidv4(),
      filterTransaction: null
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          [this.options.attributeName]: {
            default: null,
            parseHTML: (element) => element.getAttribute(`data-${this.options.attributeName}`),
            renderHTML: (attributes) => {
              if (!attributes[this.options.attributeName]) {
                return {};
              }
              return {
                [`data-${this.options.attributeName}`]: attributes[this.options.attributeName]
              };
            }
          }
        }
      }
    ];
  },
  // check initial content for missing ids
  onCreate() {
    const collab = this.editor.extensionManager.extensions.find((ext) => ext.name === "collaboration");
    const provider = (collab == null ? void 0 : collab.options) ? collab.options.provider : void 0;
    const createIds = () => {
      const { view, state } = this.editor;
      const { tr, doc } = state;
      const { types, attributeName, generateID } = this.options;
      const nodesWithoutId = findChildren(doc, (node) => {
        return types.includes(node.type.name) && node.attrs[attributeName] === null;
      });
      nodesWithoutId.forEach(({ node, pos }) => {
        tr.setNodeMarkup(pos, void 0, {
          ...node.attrs,
          [attributeName]: generateID()
        });
      });
      tr.setMeta("addToHistory", false);
      view.dispatch(tr);
      if (provider) {
        provider.off("synced", createIds);
      }
    };
    if (collab) {
      if (!provider) {
        return createIds();
      }
      provider.on("synced", createIds);
    } else {
      return createIds();
    }
  },
  addProseMirrorPlugins() {
    let dragSourceElement = null;
    let transformPasted = false;
    return [
      new Plugin({
        key: new PluginKey("uniqueID"),
        appendTransaction: (transactions, oldState, newState) => {
          const hasDocChanges = transactions.some((transaction) => transaction.docChanged) && !oldState.doc.eq(newState.doc);
          const filterTransactions = this.options.filterTransaction && transactions.some((tr2) => {
            var _a, _b;
            return !((_b = (_a = this.options).filterTransaction) == null ? void 0 : _b.call(_a, tr2));
          });
          const isCollabTransaction = transactions.find((tr2) => tr2.getMeta("y-sync$"));
          if (isCollabTransaction) {
            return;
          }
          if (!hasDocChanges || filterTransactions) {
            return;
          }
          const { tr } = newState;
          const { types, attributeName, generateID } = this.options;
          const transform = combineTransactionSteps(oldState.doc, transactions);
          const { mapping } = transform;
          const changes = getChangedRanges(transform);
          changes.forEach(({ newRange }) => {
            const newNodes = findChildrenInRange(newState.doc, newRange, (node) => {
              return types.includes(node.type.name);
            });
            const newIds = newNodes.map(({ node }) => node.attrs[attributeName]).filter((id) => id !== null);
            newNodes.forEach(({ node, pos }, i) => {
              var _a;
              const id = (_a = tr.doc.nodeAt(pos)) == null ? void 0 : _a.attrs[attributeName];
              if (id === null) {
                tr.setNodeMarkup(pos, void 0, {
                  ...node.attrs,
                  [attributeName]: generateID()
                });
                return;
              }
              const nextNode = newNodes[i + 1];
              if (nextNode && node.content.size === 0) {
                tr.setNodeMarkup(nextNode.pos, void 0, {
                  ...nextNode.node.attrs,
                  [attributeName]: id
                });
                newIds[i + 1] = id;
                if (nextNode.node.attrs[attributeName]) {
                  return;
                }
                const generatedId = generateID();
                tr.setNodeMarkup(pos, void 0, {
                  ...node.attrs,
                  [attributeName]: generatedId
                });
                newIds[i] = generatedId;
                return tr;
              }
              const duplicatedNewIds = findDuplicates(newIds);
              const { deleted } = mapping.invert().mapResult(pos);
              const newNode = deleted && duplicatedNewIds.includes(id);
              if (newNode) {
                tr.setNodeMarkup(pos, void 0, {
                  ...node.attrs,
                  [attributeName]: generateID()
                });
              }
            });
          });
          if (!tr.steps.length) {
            return;
          }
          tr.setStoredMarks(newState.tr.storedMarks);
          return tr;
        },
        // we register a global drag handler to track the current drag source element
        view(view) {
          const handleDragstart = (event) => {
            var _a;
            dragSourceElement = ((_a = view.dom.parentElement) == null ? void 0 : _a.contains(event.target)) ? view.dom.parentElement : null;
          };
          window.addEventListener("dragstart", handleDragstart);
          return {
            destroy() {
              window.removeEventListener("dragstart", handleDragstart);
            }
          };
        },
        props: {
          // `handleDOMEvents` is called before `transformPasted`
          // so we can do some checks before
          handleDOMEvents: {
            // only create new ids for dropped content
            // or dropped content while holding `alt`
            // or content is dragged from another editor
            drop: (view, event) => {
              var _a, _b;
              if (dragSourceElement !== view.dom.parentElement || ((_a = event.dataTransfer) == null ? void 0 : _a.effectAllowed) === "copyMove" || ((_b = event.dataTransfer) == null ? void 0 : _b.effectAllowed) === "copy") {
                dragSourceElement = null;
                transformPasted = true;
              }
              return false;
            },
            // always create new ids on pasted content
            paste: () => {
              transformPasted = true;
              return false;
            }
          },
          // we’ll remove ids for every pasted node
          // so we can create a new one within `appendTransaction`
          transformPasted: (slice) => {
            if (!transformPasted) {
              return slice;
            }
            const { types, attributeName } = this.options;
            const removeId = (fragment) => {
              const list = [];
              fragment.forEach((node) => {
                if (node.isText) {
                  list.push(node);
                  return;
                }
                if (!types.includes(node.type.name)) {
                  list.push(node.copy(removeId(node.content)));
                  return;
                }
                const nodeWithoutId = node.type.create(
                  {
                    ...node.attrs,
                    [attributeName]: null
                  },
                  removeId(node.content),
                  node.marks
                );
                list.push(nodeWithoutId);
              });
              return Fragment.from(list);
            };
            transformPasted = false;
            return new Slice(removeId(slice.content), slice.openStart, slice.openEnd);
          }
        }
      })
    ];
  }
});

// src/index.ts
var index_default = UniqueID;
export {
  UniqueID,
  index_default as default
};
//# sourceMappingURL=index.js.map